import React, { useRef, useEffect, useState } from 'react';
import { GoogleMap, Marker, Polyline, useJsApiLoader } from '@react-google-maps/api';
import { MAP_CONFIG } from '../config/maps';
import { createDriverMarkerIcon, createPickupMarkerIcon, createDestinationMarkerIcon, createRiderMarkerIcon } from '../utils/markerIcons';
import { getCurrentPosition } from '../utils/geolocation';

const libraries: ("drawing" | "geometry" | "places" | "visualization")[] = ['places', 'geometry'];

interface MapViewProps {
  center?: { lat: number; lng: number };
  zoom?: number;
  markers?: Array<{
    position: { lat: number; lng: number };
    title?: string;
    color?: string;
    icon?: any;
  }>;
  polylines?: Array<{
    path: Array<{ lat: number; lng: number }>;
    color?: string;
    strokeWidth?: number;
    lineDashPattern?: number[];
  }>;
  onMapClick?: (e: google.maps.MapMouseEvent) => void;
  onMarkerClick?: (marker: any) => void;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

const MapView: React.FC<MapViewProps> = ({
  center,
  zoom = 13,
  markers = [],
  polylines = [],
  onMapClick,
  onMarkerClick,
  style,
  children,
}) => {
  const apiKey = MAP_CONFIG.apiKey || '';
  const [geolocationCenter, setGeolocationCenter] = useState<{ lat: number; lng: number } | null>(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);


  useEffect(() => {
    if (!center && !geolocationCenter && !isGettingLocation) {
      setIsGettingLocation(true);
      getCurrentPosition(
        (position) => {
          setGeolocationCenter({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
          setIsGettingLocation(false);
        },
        (error) => {
          console.warn('Failed to get geolocation for map center:', error);
          setIsGettingLocation(false);

        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }
  }, [center, geolocationCenter, isGettingLocation]);


  useEffect(() => {
    if (import.meta.env.DEV) {
      const rawEnvKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
      console.log('🔍 Debug - Raw env key:', rawEnvKey ? rawEnvKey.substring(0, 10) + '...' : 'NOT SET');
      console.log('🔍 Debug - MAP_CONFIG.apiKey:', apiKey ? apiKey.substring(0, 10) + '...' : 'EMPTY');

      if (!apiKey) {
        console.error('❌ MapView: Google Maps API key is empty!');
        console.error('   Check: .env file exists, VITE_GOOGLE_MAPS_API_KEY is set, dev server restarted');
      } else if (apiKey.includes('YOUR_GOOGLE_MAPS') || apiKey.includes('your_') || apiKey === 'YOUR_GOOGLE_MAPS_API_KEY') {
        console.error('❌ MapView: Google Maps API key appears to be a placeholder!');
        console.error('   Current key:', apiKey);
        console.error('   Please set a real API key in .env file and restart dev server.');
      } else {
        console.log('✅ MapView: Using API key:', apiKey.substring(0, 10) + '...');
      }
    }
  }, [apiKey]);

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: apiKey,
    ...(MAP_CONFIG.mapId ? { mapIds: [MAP_CONFIG.mapId] } : {}),
    libraries,
    version: 'weekly',
  });


  useEffect(() => {
    if (isLoaded && window.google?.maps) {
      const originalWarn = console.warn;
      const originalError = console.error;

      console.warn = (...args: any[]) => {
        const message = typeof args[0] === 'string' ? args[0] : String(args[0] || '');
        if (message.includes('google.maps.Marker is deprecated') || 
            message.includes('IntersectionObserver')) {
          return; 
        }
        originalWarn.apply(console, args);
      };

      console.error = (...args: any[]) => {
        const message = typeof args[0] === 'string' ? args[0] : String(args[0] || '');
        if (message.includes('IntersectionObserver') && message.includes('parameter 1 is not of type')) {
          return; 
        }
        originalError.apply(console, args);
      };

      return () => {
        console.warn = originalWarn;
        console.error = originalError;
      };
    }
  }, [isLoaded]);

  const mapRef = useRef<google.maps.Map | null>(null);

  const containerStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    ...style,
  };


  const mapCenter = center || geolocationCenter;

  if (loadError || (!apiKey && isLoaded) || (apiKey && (apiKey.includes('YOUR_GOOGLE_MAPS') || apiKey.includes('your_') || apiKey === 'YOUR_GOOGLE_MAPS_API_KEY'))) {
    return (
      <div style={{ ...containerStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', flexDirection: 'column', gap: '16px', padding: '20px' }}>
        <div style={{ color: '#FF3B30', fontSize: '18px', fontWeight: '700', marginBottom: '8px' }}>⚠️ Google Maps Error</div>
        <div style={{ color: '#666', fontSize: '14px', textAlign: 'center', lineHeight: '1.6' }}>
          {!apiKey 
            ? 'Google Maps API key is missing. Please add VITE_GOOGLE_MAPS_API_KEY to your .env file and restart dev server.' 
            : apiKey.includes('YOUR_GOOGLE_MAPS') || apiKey.includes('your_') || apiKey === 'YOUR_GOOGLE_MAPS_API_KEY'
            ? 'Google Maps API key appears to be a placeholder. Please set a real API key in .env file and restart dev server.'
            : 'Invalid Google Maps API key. Please check your .env file and ensure the key is correct, then restart dev server.'}
        </div>
        <div style={{ color: '#999', fontSize: '12px', marginTop: '12px', textAlign: 'center' }}>
          <strong>Important:</strong> After modifying .env file, you must restart the dev server (stop with Ctrl+C and run `npm run dev` again).
        </div>
      </div>
    );
  }

  if (!isLoaded || !window.google?.maps) {
    return (
      <div style={{ ...containerStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5' }}>
        <div>Loading map...</div>
      </div>
    );
  }


  if (!mapCenter) {
    return (
      <div style={{ ...containerStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', flexDirection: 'column', gap: '16px' }}>
        <div>Getting your location...</div>
        <div style={{ color: '#666', fontSize: '14px', textAlign: 'center' }}>
          Please allow location access to display the map
        </div>
      </div>
    );
  }

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={mapCenter}
      zoom={zoom}
      options={{
        ...MAP_CONFIG.mapOptions,
        disableDefaultUI: false,
        zoomControl: true,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: true,
      }}
      onLoad={(map) => {
        mapRef.current = map;
      }}
      onClick={onMapClick}
    >
      {markers
        .filter((marker) => 
          marker.position && 
          typeof marker.position.lat === 'number' && 
          typeof marker.position.lng === 'number' &&
          !isNaN(marker.position.lat) &&
          !isNaN(marker.position.lng) &&
          isFinite(marker.position.lat) &&
          isFinite(marker.position.lng)
        )
        .map((marker, index) => {


          let icon = marker.icon;
          if (!icon && isLoaded && window.google?.maps?.Size && window.google?.maps?.Point) {
            try {

              if (marker.title?.toLowerCase().includes('your location') || marker.title?.toLowerCase().includes('driver')) {
                icon = createDriverMarkerIcon();
              } else if (marker.title?.toLowerCase().includes('pickup')) {
                icon = createPickupMarkerIcon();
              } else if (marker.title?.toLowerCase().includes('destination') || marker.title?.toLowerCase().includes('dropoff')) {
                icon = createDestinationMarkerIcon();
              } else if (marker.title?.toLowerCase().includes('rider')) {
                icon = createRiderMarkerIcon();
              }
            } catch (error) {

              console.warn('Failed to create marker icon:', error);
              icon = undefined;
            }
          }

          return (
            <Marker
              key={`marker-${index}-${marker.position.lat}-${marker.position.lng}`}
              position={marker.position}
              title={marker.title}
              icon={icon}
              onClick={() => onMarkerClick?.(marker)}
            />
          );
        })}
      {isLoaded && window.google?.maps && mapRef.current && polylines
        .filter((polyline) => {
          if (!polyline.path || !Array.isArray(polyline.path) || polyline.path.length < 2) {
            return false;
          }

          return polyline.path.every((point: any) => 
            point && 
            typeof point.lat === 'number' && 
            typeof point.lng === 'number' && 
            !isNaN(point.lat) && 
            !isNaN(point.lng) &&
            isFinite(point.lat) &&
            isFinite(point.lng)
          );
        })
        .map((polyline, index) => {

          const validPath = polyline.path
            .map((point: any) => ({ lat: Number(point.lat), lng: Number(point.lng) }));

          if (validPath.length < 2) {
            return null;
          }


          if (!mapRef.current) {
            return null;
          }

          try {
            return (
              <Polyline
                key={`polyline-${index}-${validPath.length}`}
                path={validPath}
                options={{
                  strokeColor: polyline.color || '#FF006E',
                  strokeWeight: polyline.strokeWidth || 4,
                  strokeOpacity: 0.8,
                  icons: polyline.lineDashPattern
                    ? [
                        {
                          icon: {
                            path: 'M 0,-1 0,1',
                            strokeOpacity: 1,
                            strokeWeight: 2,
                            scale: 4,
                          },
                          offset: '0',
                          repeat: '20px',
                        },
                      ]
                    : undefined,
                }}
              />
            );
          } catch (error) {

            return null;
          }
        })
        .filter(Boolean)}
      {children}
    </GoogleMap>
  );
};

export default MapView;

